import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, X, Compass, Pause, Play, Repeat } from "lucide-react";
import { useNarration } from "@/hooks/useNarration";
import { getTourVoice, tourAudioUrl } from "@/lib/api";
import { useTour } from "@/contexts/TourContext";
import { TOUR_STEPS } from "@/lib/tourSteps";

const PAD = 10;

const useTargetRect = (selectors, key) => {
  const [rect, setRect] = useState(null);
  useEffect(() => {
    let el = null;
    let scrolled = false;
    setRect(null);
    const tick = () => {
      el = el && document.body.contains(el) ? el : selectors.map((s) => document.querySelector(s)).find(Boolean);
      if (!el) return;
      if (!scrolled) {
        el.scrollIntoView({ block: "center", behavior: "smooth" });
        scrolled = true;
      }
      const r = el.getBoundingClientRect();
      setRect({ top: r.top, left: r.left, width: r.width, height: r.height });
    };
    const id = setInterval(tick, 200);
    window.addEventListener("resize", tick);
    window.addEventListener("scroll", tick, true);
    return () => {
      clearInterval(id);
      window.removeEventListener("resize", tick);
      window.removeEventListener("scroll", tick, true);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);
  return rect;
};

// Tour voice chosen by the admin: "browser" (computer voice, default) or "ai".
function useTourVoice() {
  const [voice, setVoice] = useState(null);
  useEffect(() => {
    getTourVoice().then(setVoice).catch(() => setVoice("browser"));
  }, []);
  return voice;
}

export function TourOverlay() {
  const { tour, next, prev, stop, toggleAuto } = useTour();
  const navigate = useNavigate();
  const loc = useLocation();
  const step = TOUR_STEPS[tour.step];
  const path = step.path(tour);
  const rect = useTargetRect(step.targets, `${tour.key}-${tour.step}`);
  const voice = useTourVoice();
  const { playing, toggle } = useNarration({
    src: voice === "ai" ? tourAudioUrl(tour.key, tour.step) : null,
    text: voice ? step.body(tour) : "",
    auto: tour.auto,
    onEnded: () => tour.auto && (advanceRef.current = setTimeout(() => autoRef.current && next(), 1200)),
  });
  const advanceRef = useRef(null);
  const autoRef = useRef(tour.auto);
  autoRef.current = tour.auto;
  useEffect(() => () => clearTimeout(advanceRef.current), []);

  useEffect(() => {
    if (`${loc.pathname}${loc.search}` !== path) navigate(path);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [path]);

  const last = tour.step === TOUR_STEPS.length - 1;
  return (
    <div className="pointer-events-none fixed inset-0 z-[60]" data-testid="tour-overlay">
      {rect ? (
        <div
          className="absolute rounded-sm ring-2 ring-accent-blue transition-[top,left,width,height] duration-300"
          style={{
            top: rect.top - PAD,
            left: rect.left - PAD,
            width: rect.width + PAD * 2,
            height: rect.height + PAD * 2,
            boxShadow: "0 0 0 9999px rgba(11, 20, 55, 0.55)",
          }}
          data-testid="tour-highlight"
        />
      ) : (
        <div className="absolute inset-0 bg-[rgba(11,20,55,0.35)]" />
      )}
      <TourCard step={step} tour={tour} last={last && !tour.auto} onNext={next} onPrev={prev} onStop={stop}
        narration={{ playing, toggle, toggleAuto }} />
    </div>
  );
}

function TourCard({ step, tour, last, onNext, onPrev, onStop, narration }) {
  return (
    <aside
      className="pointer-events-auto absolute inset-x-4 bottom-4 border border-slate-200 bg-white p-5 text-slate-900 shadow-2xl sm:inset-x-auto sm:bottom-6 sm:right-6 sm:w-[380px]"
      data-testid="tour-card"
    >
      <div className="flex items-start justify-between gap-3">
        <p className="eyebrow flex items-center gap-1.5 text-accent-blue">
          <Compass className="h-3 w-3" /> Tour · {tour.company} · {tour.step + 1}/{TOUR_STEPS.length}
        </p>
        <button onClick={onStop} className="text-slate-400 hover:text-slate-900" aria-label="Salir del tour" data-testid="tour-close-btn">
          <X className="h-4 w-4" />
        </button>
      </div>
      <h3 className="mt-2 font-display text-xl font-medium" data-testid="tour-step-title">{step.title}</h3>
      <p className="mt-2 text-sm leading-relaxed text-slate-600" data-testid="tour-step-body">{step.body(tour)}</p>
      <div className="mt-4 flex items-center gap-2">
        <button
          onClick={narration.toggle}
          className="inline-flex items-center gap-1.5 border border-slate-300 px-3 py-1.5 text-[10px] uppercase tracking-[0.18em] text-slate-700 hover:border-slate-900"
          data-testid="tour-narration-btn"
        >
          {narration.playing ? <Pause className="h-3 w-3" /> : <Play className="h-3 w-3" />}
          {narration.playing ? "Pausar" : "Escuchar"}
        </button>
        <button
          onClick={narration.toggleAuto}
          aria-pressed={!!tour.auto}
          className={`inline-flex items-center gap-1.5 border px-3 py-1.5 text-[10px] uppercase tracking-[0.18em] ${
            tour.auto ? "border-accent-blue bg-accent-blue text-white" : "border-slate-300 text-slate-700 hover:border-slate-900"
          }`}
          data-testid="tour-auto-btn"
        >
          <Repeat className="h-3 w-3" /> {tour.auto ? "Automático" : "Auto"}
        </button>
      </div>
      <div className="mt-4 flex gap-1" aria-hidden="true">
        {TOUR_STEPS.map((s, i) => (
          <span key={s.title} className={`h-1 flex-1 ${i <= tour.step ? "bg-accent-blue" : "bg-slate-200"}`} />
        ))}
      </div>
      <div className="mt-4 flex items-center justify-between">
        <button
          onClick={onPrev}
          disabled={tour.step === 0}
          className="inline-flex items-center gap-1 text-[11px] uppercase tracking-[0.18em] text-slate-500 hover:text-slate-900 disabled:opacity-30"
          data-testid="tour-prev-btn"
        >
          <ArrowLeft className="h-3 w-3" /> Anterior
        </button>
        <button
          onClick={onNext}
          className="inline-flex items-center gap-1 bg-slate-900 px-4 py-2 text-[11px] uppercase tracking-[0.18em] text-white transition-colors hover:bg-slate-700"
          data-testid="tour-next-btn"
        >
          {last ? "Finalizar" : "Siguiente"} {!last && <ArrowRight className="h-3 w-3" />}
        </button>
      </div>
    </aside>
  );
}
